/**
 * QueryErrorBoundary - TanStack Query用のエラーバウンダリ
 */

import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { QueryErrorResetBoundary } from '@tanstack/react-query'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryInner extends Component<Props & { reset: () => void }, State> {
  constructor(props: Props & { reset: () => void }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Query Error Boundary caught:', error, errorInfo)
  }

  componentDidUpdate(prevProps: Props & { reset: () => void }) {
    if (this.state.hasError && prevProps.reset !== this.props.reset) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, () => {
          this.props.reset()
          this.setState({ hasError: false, error: null })
        })
      }

      // デフォルトのエラー表示
      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-lg p-6 shadow-xl">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-red-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-xl font-bold">エラーが発生しました</h2>
            </div>
            
            <p className="text-gray-400 mb-4">
              データの取得中に問題が発生しました。
            </p>
            
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-300">
                詳細情報
              </summary>
              <pre className="mt-2 text-xs bg-gray-900 p-2 rounded overflow-auto max-h-32">
                {this.state.error.message}
              </pre>
            </details>
            
            <button
              onClick={() => {
                this.props.reset()
                this.setState({ hasError: false, error: null })
              }}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              再試行
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * TanStack Query対応のエラーバウンダリ
 */
export function QueryErrorBoundary({ children, fallback }: Props) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundaryInner reset={reset} fallback={fallback}>
          {children}
        </ErrorBoundaryInner>
      )}
    </QueryErrorResetBoundary>
  )
}